import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import adUnitService from "../../services/adUnitService";

interface AdUnit {
  id: number;
  user_id: number;
  name: string;
  ad_type: string;
  size: string;
  description?: string;
  ad_code?: any;
  settings?: any;
  revenue: number;
  impressions: number;
  clicks: number;
  ctr: number;
  status: string;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
}

interface AdUnitState {
  adUnits: AdUnit[];
  summary: any;
  statistics: any;
  isLoading: boolean;
  error: string | null;
}

const initialState: AdUnitState = {
  adUnits: [],
  summary: null,
  statistics: null,
  isLoading: false,
  error: null,
};

// Fixed: Using object parameter
export const fetchAdUnits = createAsyncThunk(
  "adUnits/fetchAdUnits",
  async (params: any = {}, { rejectWithValue }) => {
    try {
      const response = await adUnitService.getAdUnits(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to fetch ad units"
      );
    }
  }
);

export const fetchAdUnitStatistics = createAsyncThunk(
  "adUnits/fetchStatistics",
  async (_, { rejectWithValue }) => {
    try {
      const response = await adUnitService.getAdUnitStatistics();
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to fetch statistics"
      );
    }
  }
);

export const createAdUnit = createAsyncThunk(
  "adUnits/createAdUnit",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await adUnitService.createAdUnit(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to create ad unit"
      );
    }
  }
);

export const updateAdUnit = createAsyncThunk(
  "adUnits/updateAdUnit",
  async ({ id, data }: { id: number; data: any }, { rejectWithValue }) => {
    try {
      const response = await adUnitService.updateAdUnit(id, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to update ad unit"
      );
    }
  }
);

export const deleteAdUnit = createAsyncThunk(
  "adUnits/deleteAdUnit",
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await adUnitService.deleteAdUnit(id);
      return { id, ...response };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to delete ad unit"
      );
    }
  }
);

export const bulkUpdateAdUnits = createAsyncThunk(
  "adUnits/bulkUpdate",
  async (data: { ids: number[]; status: string }, { rejectWithValue }) => {
    try {
      const response = await adUnitService.bulkUpdateAdUnits(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to bulk update ad units"
      );
    }
  }
);

const adUnitSlice = createSlice({
  name: "adUnits",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdUnits.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchAdUnits.fulfilled, (state, action) => {
        state.isLoading = false;
        state.adUnits = action.payload.data || [];
        state.summary = action.payload.summary || null;
      })
      .addCase(fetchAdUnits.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      .addCase(fetchAdUnitStatistics.fulfilled, (state, action) => {
        state.statistics = action.payload;
      })

      .addCase(createAdUnit.fulfilled, (state, action) => {
        if (action.payload.data) {
          state.adUnits.unshift(action.payload.data);
          state.summary.total = (state.summary?.total || 0) + 1;
          state.summary.active = (state.summary?.active || 0) + 1;
        }
      })

      .addCase(updateAdUnit.fulfilled, (state, action) => {
        const updatedAdUnit = action.payload.data;
        if (updatedAdUnit) {
          const index = state.adUnits.findIndex(
            (unit) => unit.id === updatedAdUnit.id
          );
          if (index !== -1) {
            state.adUnits[index] = updatedAdUnit;
          }
        }
      })

      .addCase(deleteAdUnit.fulfilled, (state, action) => {
        const id = action.meta.arg;
        state.adUnits = state.adUnits.filter((unit) => unit.id !== id);
        state.summary.total = (state.summary?.total || 1) - 1;
      })

      .addCase(bulkUpdateAdUnits.fulfilled, (state, action) => {
        // Update status for bulk updated ad units
        const { ids, status } = action.meta.arg;
        state.adUnits = state.adUnits.map((unit) => {
          if (ids.includes(unit.id)) {
            return { ...unit, status };
          }
          return unit;
        });
      });
  },
});

export const { clearError } = adUnitSlice.actions;
export default adUnitSlice.reducer;
